import { useCallback, useEffect, useMemo, useState } from 'react';
import { useCalendarDateList } from './useCalendarDateList';

interface CalendarDateItemHookProps {
  monthDate: Date;
  reserveItemList: {
    [date_block: string]: Date[];
  }[];
  reserveDate: Date;
}
export const useCalendarDateItem = ({
  monthDate,
  reserveItemList,
  reserveDate,
}: CalendarDateItemHookProps) => {
  const { dateItemList } = useCalendarDateList({ month: monthDate });

  const [dateSelect, setDateSelect] = useState('');
  useEffect(() => {
    setDateSelect(toDateText(reserveDate));
  }, [reserveDate]);

  const padding = useCallback((value: number) => {
    const zeroPad = 2;
    return value.toString().padStart(zeroPad, '0');
  }, []);
  const toDateText = useCallback((date: Date) => {
    const offset = 1;
    return `${date.getFullYear()}-${padding(
      date.getMonth() + offset,
    )}-${padding(date.getDate())}`;
  }, []);

  const handleChangeDate = useCallback(
    (date: Date) => setDateSelect(toDateText(date)),
    [setDateSelect],
  );

  const getSelectDateStyle = useCallback(
    (calendarDate: Date, reserve: number) => {
      const current = new Date();
      if (calendarDate.getTime() > current.getTime()) {
        if (reserve > 0) {
          if (reserveDate instanceof Date && !isNaN(reserveDate.getTime())) {
            if (
              calendarDate.getFullYear() === reserveDate.getFullYear() &&
              calendarDate.getMonth() === reserveDate.getMonth() &&
              calendarDate.getDate() === reserveDate.getDate()
            ) {
              return 'date-item select';
            } else {
              return 'date-item';
            }
          } else {
            return 'date-item';
          }
        } else {
          return 'date-item invalid';
        }
      } else {
        return 'date-item closed';
      }
    },
    [reserveDate],
  );
  const getReserveDateStyle = useCallback(
    (calendarDate: Date, reserve: number) => {
      const fewReserve = 3;
      const current = new Date();
      if (calendarDate.getMonth() === monthDate.getMonth()) {
        if (calendarDate.getTime() > current.getTime()) {
          if (reserve <= 0) {
            return 'date-item-status no-available';
          } else if (reserve < fewReserve) {
            return 'date-item-status few-available';
          } else if (reserve >= fewReserve) {
            return 'date-item-status available';
          } else {
            return 'date-item-status closed';
          }
        } else {
          return 'date-item-status closed';
        }
      } else {
        return 'date-item-status closed';
      }
    },
    [monthDate],
  );
  const getAvailableReserves = useCallback(
    (date: Date) => {
      const keyName = toDateText(date);
      const findItem = reserveItemList.find((item) =>
        Object.keys(item).includes(keyName),
      );
      const available = findItem ? findItem[keyName].length : 0;
      return available;
    },
    [reserveItemList],
  );

  const dateList = useMemo(() => {
    const list = dateItemList.reduce((calendarList, weekItem) => {
      const weekList = weekItem.reduce((dateList, dateItem) => {
        const reserves = getAvailableReserves(dateItem);
        const item = {
          dateLabel: dateItem.getDate().toString(),
          selectStyle: getSelectDateStyle(dateItem, reserves),
          reserveStyle: getReserveDateStyle(dateItem, reserves),
          onClick: () => reserves > 0 && handleChangeDate(dateItem),
        };
        dateList.push(item);
        return dateList;
      }, [] as CalendarDateItemType[]);
      calendarList.push(weekList);
      return calendarList;
    }, [] as CalendarDateItemType[][]);
    return list;
  }, [
    dateItemList,
    getAvailableReserves,
    getSelectDateStyle,
    getReserveDateStyle,
    handleChangeDate,
  ]);

  return { dateSelect, dateList };
};

export type CalendarDateItemType = {
  dateLabel: string;
  selectStyle: string;
  reserveStyle: string;
  onClick: () => void;
};
